
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Heart, Eye } from "lucide-react";
// Image component removed - use regular img tag
import { Link } from "react-router-dom";
import { getSmartProductPlaceholder } from "@/lib/placeholders";
import { productsApi } from "@/lib/api";
import { useWishlistStore } from "@/lib/store";
import { useAuthStore } from "@/lib/store";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";
import toast from "react-hot-toast";

interface ProductImage {
  id: number;
  image_url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

interface Product {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  original_price: number | null;
  image_url: string | null;
  stock: number;
  order_count: number;
  is_active: boolean;
  is_featured: boolean;
  images?: ProductImage[];
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [pendingProductId, setPendingProductId] = useState<number | null>(null);

  const { addItem: addToWishlist, isInWishlist, removeItem: removeFromWishlist, addItemToBackend, removeItemFromBackend } = useWishlistStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const fetchBestSellingProducts = async () => {
      try {
        setLoading(true);
        const response = await productsApi.getBestSelling(10);
        const productsData = response.products || [];
        setProducts(productsData);
        console.log("Best selling products loaded:", productsData.length);
      } catch (error: any) {
        console.error("Error fetching best selling products:", error);
        toast.error("Failed to load products");
        // Keep empty array to show section even on error
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBestSellingProducts();
  }, []);

  // Calculate discount percentage
  const calculateDiscount = (price: number, originalPrice: number | null) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  const handleWishlistToggle = async (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Get fresh token from store (in case it was just updated)
    const currentToken = useAuthStore.getState().token;
    const currentIsAuthenticated = useAuthStore.getState().isAuthenticated;

    if (!currentIsAuthenticated) {
      setPendingProductId(product.id);
      setShowLoginDialog(true);
      return;
    }

    if (!currentToken) {
      toast.error("Please log in to add items to wishlist");
      return;
    }

    try {
      const primaryImage = product.images?.find((img) => img.is_primary);
      if (isInWishlist(product.id)) {
        await removeItemFromBackend(product.id, currentToken);
        removeFromWishlist(product.id);
        toast.success("Removed from wishlist");
      } else {
        const success = await addItemToBackend(product.id, currentToken);
        if (success) {
          addToWishlist({
            id: product.id,
            name: product.name,
            price: product.price,
            originalPrice: product.original_price ?? undefined,
            image: primaryImage?.image_url || product.image_url || "",
            slug: product.slug,
          });
          toast.success("Added to wishlist!", {
            duration: 3000,
            position: "top-center",
          });
        } else {
          toast.error("Failed to add to wishlist");
        }
      }
    } catch (error: any) {
      console.error("Wishlist error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      if (error.response?.status === 401) {
        const errorDetail = error.response?.data?.detail || "Invalid authentication credentials";
        toast.error(errorDetail);
        // Don't logout automatically - let user try again with fresh login
        console.error("Authentication failed. Token used:", currentToken?.substring(0, 20) + "...");
      } else {
        toast.error(error.response?.data?.detail || "Failed to update wishlist");
      }
    }
  };

  const handleLoginSuccess = async () => {
    // Get fresh token after login
    const authState = useAuthStore.getState();
    const freshToken = authState.token;
    
    if (pendingProductId && freshToken) {
      const product = products.find((p) => p.id === pendingProductId);
      if (product) {
        const primaryImage = product.images?.find((img) => img.is_primary);
        try {
          const success = await addItemToBackend(product.id, freshToken);
          if (success) {
            addToWishlist({
              id: product.id,
              name: product.name,
              price: product.price,
              originalPrice: product.original_price ?? undefined,
              image: primaryImage?.image_url || product.image_url || "",
              slug: product.slug,
            });
            toast.success("Added to wishlist!", {
              duration: 3000,
              position: "top-center",
            });
          }
        } catch (error: any) {
          console.error("Failed to add to wishlist after login:", error);
          toast.error(error.response?.data?.detail || "Failed to add to wishlist");
        }
      }
      setPendingProductId(null);
    }
  };

  if (loading) {
    return (
      <section id="best-selling" className="py-12 bg-white mt-4 mb-4 scroll-mt-24">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
              Best Selling <span className="gradient-text">Products</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Discover our most loved pieces, chosen by thousands of satisfied customers
            </p>
          </motion.div>

          {/* Skeleton Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative"
              >
                <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                  <div className="w-full h-full bg-gray-200 animate-pulse" />
                </div>
                <div className="mt-3 space-y-2">
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                  <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-5 w-1/2 bg-gray-200 rounded animate-pulse" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show section even if no products (for debugging)
  // if (products.length === 0) {
  //   return null;
  // }
  return (
    <section id="best-selling" className="py-12 bg-white mt-4 mb-4 scroll-mt-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
            Best Selling <span className="gradient-text">Products</span>
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Discover our most loved pieces, chosen by thousands of satisfied customers
          </p>
        </motion.div>

        {products.length === 0 && !loading && (
          <div className="text-center text-gray-500 mb-4">
            No products available
          </div>
        )}
        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
          {products.map((product, index) => {
            const discount = calculateDiscount(product.price, product.original_price);
            // Get primary image from images array, or fallback to image_url
            const primaryImage = product.images?.find(img => img.is_primary);
            const imageUrl = primaryImage?.image_url || product.image_url || getSmartProductPlaceholder(product.name);
            
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <Link to={`/products/${product.slug}`}>
                  <div className="relative overflow-hidden rounded-2xl bg-gray-100 aspect-square">
                    {/* Product Image */}
                    <img
                      src={imageUrl}
                      alt={product.name}
                      className={`object-cover w-full h-full transition-opacity duration-300 ${
                        product.stock === 0 ? "opacity-50" : "opacity-100"
                      }`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = getSmartProductPlaceholder(product.name);
                      }}
                    />

                    {/* Sold Out Badge - Top Right */}
                    {product.stock === 0 && (
                      <div className="absolute top-2 right-2 z-20">
                        <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-black text-white shadow-sm">
                          Sold out
                        </span>
                      </div>
                    )}

                    {/* Badge - Best Seller for top products */}
                    {index < 3 && product.stock > 0 && (
                      <div className="absolute top-2 left-2 z-10">
                        <span className="px-2 py-1 rounded-md text-[10px] font-medium bg-gradient-to-r from-aarsa-gold-500 to-aarsa-gold-600 text-white shadow-sm">
                          Best Seller
                        </span>
                      </div>
                    )}

                    {/* Hover Overlay - Only show if product is in stock */}
                    {product.stock > 0 && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100 z-10">
                        <div className="flex space-x-4">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors"
                            aria-label="Quick view"
                          >
                            <Eye className="w-5 h-5 text-gray-900" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => handleWishlistToggle(product, e)}
                            className={`p-3 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors ${
                              isInWishlist(product.id) ? "text-red-500" : "text-gray-900"
                            }`}
                            aria-label="Add to wishlist"
                          >
                            <Heart className={`w-5 h-5 ${isInWishlist(product.id) ? "-current" : ""}`} />
                          </motion.button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="mt-4 space-y-2">
                    <h3 className="font-semibold text-gray-900 group-hover:text-aarsa-gold-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className="text-xl font-bold text-gray-900">
                        ₹{product.price.toLocaleString()}
                      </span>
                      {product.original_price && discount > 0 && (
                        <>
                          <span className="text-sm text-gray-500 line-through">
                            ₹{product.original_price.toLocaleString()}
                          </span>
                          <span className="text-sm font-semibold text-green-600">
                            Save {discount}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <Link to="/products">
            <button
              type="button"
              className="group relative px-10 py-2.5 rounded-full bg-gradient-to-r from-aarsa-sage-600 to-aarsa-sage-700 text-white font-bold text-lg shadow-lg hover:shadow-xl hover:from-aarsa-sage-700 hover:to-aarsa-sage-800 transition-all cursor-pointer overflow-hidden"
            >
              {/* Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              <span className="relative z-10">View All</span>
            </button>
          </Link>
        </motion.div>
      </div>

      {/* OTP Login Dialog */}
      <OTPLoginDialog
        isOpen={showLoginDialog}
        onClose={() => {
          setShowLoginDialog(false);
          setPendingProductId(null);
        }}
        onSuccess={handleLoginSuccess}
      />
    </section>
  );
}
